/* Pull requests, in the prototype's layout: a heading and its sentence beside a search
   box, a row of counted status filters, then one table inside one panel with the pager in
   its footer.

   A row is a review job -- one delivery, one commit -- not a pull request. A pull request
   pushed to three times has three reviews with three outcomes, and collapsing them would
   mean choosing one of them to show. */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, query } from "@/api";
import { useOrgPath } from "@/session";
import { useApi } from "@/useApi";
import {
  Async,
  FilterPill,
  GitMark,
  Pager,
  SearchBox,
  Segments,
  Severity,
  StateDot,
  Table,
  panel,
  relativeTime,
  stateLabel,
  useDebounced,
} from "@/ui";

const STATES = ["completed", "queued", "waiting_for_index", "skipped", "stale", "failed"];
const FILTERS = [["", "All"], ...STATES.map((value) => [value, stateLabel(value)])];

export function Pulls() {
  const orgPath = useOrgPath();
  const navigate = useNavigate();
  const [state, setState] = useState("");
  const [repositoryId, setRepositoryId] = useState(null);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const settled = useDebounced(search);

  const repositories = useApi(
    (signal) => api.get(`${orgPath}/repositories${query({ limit: 100 })}`, { signal }),
    [orgPath],
    { skip: !orgPath },
  );

  const pulls = useApi(
    (signal) =>
      api.get(
        `${orgPath}/pulls${query({
          state: state || undefined,
          repository_id: repositoryId,
          search: settled,
          offset,
        })}`,
        { signal },
      ),
    [orgPath, state, repositoryId, settled, offset],
    { skip: !orgPath },
  );

  const byState = pulls.data?.countsByState ?? {};
  const counts = { ...byState, "": pulls.data?.total ?? 0 };

  return (
    <>
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ flex: 1, minWidth: "220px" }}>
          <span
            style={{
              display: "block",
              font: "var(--text-h4)",
              color: "var(--text-primary)",
              letterSpacing: "-0.02em",
              marginBottom: "5px",
            }}
          >
            Pull requests
          </span>
          <span style={{ font: "var(--text-body-14)", color: "var(--text-muted)" }}>
            Every pull request CodeNerve has seen, with what it found.
          </span>
        </span>
        <SearchBox
          value={search}
          onChange={(value) => {
            setSearch(value);
            setOffset(0);
          }}
          placeholder="Search pull requests"
        />
      </div>

      <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", alignItems: "center" }}>
        <Segments
          value={state}
          options={FILTERS}
          counts={counts}
          onChange={(next) => {
            setState(next);
            setOffset(0);
          }}
        />
        <FilterPill
          label="All repositories"
          value={repositoryId}
          onChange={(value) => {
            setRepositoryId(value);
            setOffset(0);
          }}
          options={(repositories.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />
      </div>

      <div style={{ ...panel, overflow: "hidden" }}>
        <Async state={pulls}>
          {(page) => (
            <>
              <Table
                minWidth="720px"
                onRowClick={(row) => navigate(`/pulls/${row.id}`)}
                empty="No pull requests have been reviewed yet."
                rows={page.items}
                columns={[
                  {
                    key: "title",
                    header: "Pull request",
                    render: (row) => (
                      <>
                        <span style={{ display: "block", color: "var(--text-primary)" }}>
                          {row.title || "Untitled"}
                        </span>
                        <span
                          style={{
                            font: "var(--text-caption)",
                            fontFamily: "var(--font-mono)",
                            color: "var(--text-muted)",
                          }}
                        >
                          #{row.number}
                        </span>
                      </>
                    ),
                  },
                  {
                    key: "repository",
                    header: "Repository",
                    nowrap: true,
                    render: (row) => (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                        <GitMark size={13} />
                        {row.repository}
                      </span>
                    ),
                  },
                  {
                    key: "author",
                    header: "Author",
                    nowrap: true,
                    render: (row) => (
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "12px",
                          color: "var(--text-muted)",
                        }}
                      >
                        {row.author || "—"}
                      </span>
                    ),
                  },
                  {
                    key: "findings",
                    header: "Findings",
                    nowrap: true,
                    render: (row) =>
                      row.findingCount === 0 ? (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                          {row.findingCount}
                          <Severity level={row.topSeverity} />
                        </span>
                      ),
                  },
                  {
                    key: "state",
                    header: "Status",
                    nowrap: true,
                    render: (row) => <StateDot state={row.state} />,
                  },
                  {
                    key: "updated",
                    header: "Updated",
                    align: "right",
                    nowrap: true,
                    render: (row) => (
                      <span style={{ color: "var(--text-muted)" }}>
                        {relativeTime(row.finishedAt ?? row.createdAt)}
                      </span>
                    ),
                  },
                ]}
              />
              <Pager page={page} onChange={setOffset} />
            </>
          )}
        </Async>
      </div>
    </>
  );
}
